/**
 * Chat primitives in the shadcn mould: a scroller that stays pinned to the
 * newest message, and a message with a role. Deliberately free of threads,
 * providers and stores, so any small conversation can wear them — the main
 * chat's timeline is a different, much heavier thing.
 *
 * @module components/ui/message
 */
import { useEffect, useRef, type ComponentProps, type ReactNode } from "react";

import { cn } from "~/lib/utils";

/**
 * Scrolls its content and follows the bottom while the reader is there. Scroll
 * up to read and it lets go; come back to the end and it follows again.
 * `follow` should change whenever content grows (e.g. the streamed text).
 */
export function MessageScroller({
  follow,
  className,
  children,
  ...props
}: ComponentProps<"div"> & { readonly follow: unknown }) {
  const ref = useRef<HTMLDivElement>(null);
  const pinned = useRef(true);

  useEffect(() => {
    const element = ref.current;
    if (element && pinned.current) element.scrollTop = element.scrollHeight;
  }, [follow]);

  return (
    <div
      ref={ref}
      data-slot="message-scroller"
      onScroll={(event) => {
        const element = event.currentTarget;
        pinned.current = element.scrollHeight - element.scrollTop - element.clientHeight < 32;
      }}
      className={cn("min-h-0 flex-1 overflow-y-auto overscroll-contain", className)}
      {...props}
    >
      {children}
    </div>
  );
}

export function Message({
  from,
  className,
  ...props
}: ComponentProps<"div"> & { readonly from: "user" | "assistant" }) {
  return (
    <div
      data-slot="message"
      data-from={from}
      className={cn("group/message flex w-full", from === "user" && "justify-end", className)}
      {...props}
    />
  );
}

export function MessageContent({
  className,
  children,
  ...props
}: ComponentProps<"div"> & { readonly children: ReactNode }) {
  return (
    <div
      data-slot="message-content"
      className={cn(
        "min-w-0 text-sm leading-6",
        "group-data-[from=user]/message:max-w-[85%] group-data-[from=user]/message:rounded-2xl group-data-[from=user]/message:rounded-br-md group-data-[from=user]/message:bg-secondary group-data-[from=user]/message:px-3 group-data-[from=user]/message:py-1.5 group-data-[from=user]/message:text-secondary-foreground",
        "group-data-[from=assistant]/message:w-full group-data-[from=assistant]/message:text-foreground",
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}
